import { Group, Text, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

/**
 * One wording per metric, shared by the billing table, its detail modal and the firm card, so the
 * same number cannot be explained three different ways.
 */
const METRIC_HINTS = {
  billedUnits:
    'A unit is a family, counted once however many people are in it, or a client with no family. Billed units are the ones holding an active client — the bill is drawn from these.',
  allUnits:
    'Every unit on the platform, active or not: each family counted once, plus each client belonging to no family.',
  activeClients:
    'People, not units: clients with at least two pieces of activity against them — a meeting note, agenda, suitability report, Siesta conversation, recording, or an upload that had detections run on it. A couple who are both active are two active clients but one billed unit.',
  allClients: 'Every client on the platform, active or not. Test clients are excluded throughout.',
  statedFamilies:
    'How many families the firm told us it has, counting a client in no family as one. Entered on the billing form.',
  billingAmount:
    'The price per unit for a per-unit firm, or the flat monthly fee for a fixed firm. The billing type column says which.',
  monthlyRecurringRevenue:
    "Paying adviser firms' bills this month plus what the strategic partner pays today, entered from the Strategic partner button. A year is twelve months of it; everything is billed monthly.",
  fullAdoption:
    "Paying adviser firms with every stated family at £5, never below what they pay today, plus the strategic partner's build fee and license for every one of its customers. Firms that have not stated their families are held at today's bill.",
  pipeline:
    'Firms with a login whose billing has not started yet, at the price on their billing form. A forecast, not contracted revenue.',
  modelledRevenue:
    'Monthly recurring revenue under the assumptions on the left, priced by the same rules as the Revenue tab. A year is twelve months of it.',
  actionsByCustomers:
    'SR creates, meeting notes, agendas, meetings recorded, files uploaded and Siesta messages, plus new clients added, in the month selected above.',
} as const;

export type MetricHintKey = keyof typeof METRIC_HINTS;

export function MetricHint({ hint }: { hint: MetricHintKey }) {
  return (
    <Tooltip label={METRIC_HINTS[hint]} multiline maw={280} withArrow>
      <IconInfoCircle size={14} color="var(--mantine-color-dimmed)" style={{ cursor: 'help' }} />
    </Tooltip>
  );
}

export function HintedLabel({ label, hint }: { label: string; hint: MetricHintKey }) {
  return (
    <Group gap={4} wrap="nowrap" style={{ display: 'inline-flex' }}>
      <Text span inherit>
        {label}
      </Text>
      <MetricHint hint={hint} />
    </Group>
  );
}
